import { styled } from '@mui/material';

export const DashboardSurface = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export const KpiGrid = styled('div')(({ theme }) => ({
  background: theme.palette.divider,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 6,
  display: 'grid',
  gap: 1,
  gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
  overflow: 'hidden',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },

  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const KpiCard = styled('article')(({ theme }) => ({
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.35),
  minHeight: 88,
  padding: theme.spacing(1.25, 1.4),

  '& .MuiTypography-caption': {
    color: '#64748b',
    fontSize: '0.68rem',
    fontWeight: 700,
    lineHeight: 1.2,
    textTransform: 'uppercase',
  },

  '& .MuiTypography-h4': {
    color: '#172033',
    fontSize: '1.28rem',
    fontWeight: 800,
    lineHeight: 1.1,
    overflowWrap: 'anywhere',
  },

  '& .MuiTypography-body2': {
    color: '#7b8495',
    fontSize: '0.75rem',
    lineHeight: 1.2,
  },
}));

export const AnalyticsGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1.25),
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },

  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const Panel = styled('section', {
  shouldForwardProp: prop => prop !== '$wide',
})<{ $wide?: boolean }>(({ theme, $wide }) => ({
  background: '#fff',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 6,
  boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)',
  gridColumn: $wide ? 'span 2' : 'auto',
  minHeight: 272,
  overflow: 'hidden',

  [theme.breakpoints.down('md')]: {
    gridColumn: 'auto',
  },
}));

export const PanelHeader = styled('div')(({ theme }) => ({
  alignItems: 'center',
  background: '#fbfcfe',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  minHeight: 44,
  padding: theme.spacing(1, 1.4),

  '& .MuiTypography-subtitle1': {
    color: '#263247',
    fontSize: '0.82rem',
    fontWeight: 800,
  },
}));

export const PanelTotal = styled('span')({
  color: '#64748b',
  fontSize: '0.72rem',
  fontWeight: 700,
});

export const TimelineChart = styled('div')(({ theme }) => ({
  alignItems: 'stretch',
  display: 'flex',
  gap: theme.spacing(0.6),
  height: 226,
  overflowX: 'auto',
  padding: theme.spacing(1.25, 1.4, 1),
}));

export const TimelineColumn = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flex: '1 0 48px',
  flexDirection: 'column',
  minWidth: 0,
});

export const TimelineValue = styled('span')({
  color: '#536076',
  fontSize: '0.59rem',
  fontWeight: 700,
  height: 24,
  maxWidth: '100%',
  overflow: 'hidden',
  textAlign: 'center',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const TimelineTrack = styled('div')({
  alignItems: 'flex-end',
  borderBottom: '1px solid #d8dee8',
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  minHeight: 0,
  width: '100%',
});

export const TimelineBar = styled('div')({
  background: '#841449',
  borderRadius: '2px 2px 0 0',
  maxWidth: 28,
  minHeight: 4,
  opacity: 0.88,
  transition: 'height 220ms ease',
  width: '62%',
});

export const TimelineLabel = styled('span')({
  color: '#7b8495',
  fontSize: '0.62rem',
  marginTop: 6,
});

export const StatusBody = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: '132px minmax(0, 1fr)',
  minHeight: 226,
  padding: theme.spacing(1.5),

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    justifyItems: 'center',
  },
}));

export const Donut = styled('div', {
  shouldForwardProp: prop => prop !== '$closed',
})<{ $closed: number }>(({ $closed }) => ({
  alignItems: 'center',
  background: `conic-gradient(#18864b 0 ${$closed}%, #d8a13b ${$closed}% 100%)`,
  borderRadius: '50%',
  display: 'flex',
  height: 124,
  justifyContent: 'center',
  position: 'relative',
  width: 124,

  '&::after': {
    background: '#fff',
    borderRadius: '50%',
    content: '""',
    height: 82,
    position: 'absolute',
    width: 82,
  },
}));

export const DonutCenter = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  zIndex: 1,

  '& strong': {
    color: '#172033',
    fontSize: '1rem',
  },

  '& span': {
    color: '#7b8495',
    fontSize: '0.62rem',
  },
});

export const StatusLegend = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1.15),
  width: '100%',
}));

export const LegendItem = styled('div', {
  shouldForwardProp: prop => prop !== '$color',
})<{ $color: string }>(({ $color }) => ({
  alignItems: 'center',
  borderBottom: '1px solid #edf0f4',
  display: 'grid',
  gap: 8,
  gridTemplateColumns: '8px minmax(0, 1fr) auto',
  paddingBottom: 8,

  '&::before': {
    background: $color,
    borderRadius: 2,
    content: '""',
    height: 8,
    width: 8,
  },

  '& span': {
    color: '#64748b',
    fontSize: '0.72rem',
  },

  '& strong': {
    color: '#263247',
    fontSize: '0.76rem',
  },
}));

export const RankingList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.05),
  padding: theme.spacing(1.35),
}));

export const RankingItemRow = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'grid',
  gap: theme.spacing(0.5, 1),
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  minWidth: 0,
}));

export const RankingLabel = styled('span')({
  color: '#435067',
  fontSize: '0.72rem',
  fontWeight: 700,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const RankingValue = styled('span')({
  color: '#263247',
  fontSize: '0.68rem',
  fontWeight: 800,
});

export const RankingTrack = styled('div')({
  background: '#edf0f4',
  borderRadius: 2,
  height: 8,
  overflow: 'hidden',
});

export const RankingBar = styled('div')({
  background: '#841449',
  height: '100%',
});

export const RankingCount = styled('span')({
  color: '#7b8495',
  fontSize: '0.65rem',
  minWidth: 22,
  textAlign: 'right',
});

export const FinancialGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  minHeight: 226,
});

export const FinancialItem = styled('div')({
  borderBottom: '1px solid #edf0f4',
  borderRight: '1px solid #edf0f4',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minWidth: 0,
  padding: 14,

  '&:nth-of-type(even)': {
    borderRight: 0,
  },

  '&:nth-last-of-type(-n + 2)': {
    borderBottom: 0,
  },

  '& span': {
    color: '#7b8495',
    fontSize: '0.68rem',
  },

  '& strong': {
    color: '#263247',
    fontSize: '0.88rem',
    marginTop: 5,
    overflowWrap: 'anywhere',
  },
});

export const EmptyState = styled('div')({
  alignItems: 'center',
  color: '#8a93a3',
  display: 'flex',
  fontSize: '0.75rem',
  gridColumn: '1 / -1',
  justifyContent: 'center',
  minHeight: 120,
  width: '100%',
});
